import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Clock, X, Package, ArrowRight } from 'lucide-react';
import { getRecentlyViewedIds, clearRecentlyViewed } from '../../lib/recentlyViewed';
import { listingUrl } from '../../lib/listingUrl';

const API_URL = (() => { const e = process.env.REACT_APP_BACKEND_URL; if (!e) return window.location.origin; try { const eh = new URL(e).hostname; const ch = window.location.hostname; if (ch === 'localhost' || ch === '127.0.0.1' || eh === ch || ch.includes('preview.emergentagent.com')) return e; return window.location.origin; } catch { return window.location.origin; } })();

const GRADE_BG = {
  A: 'bg-emerald-500 text-white',
  B: 'bg-amber-500 text-white',
  C: 'bg-orange-500 text-white'
};

/**
 * "Recently Viewed" strip, surfaces devices the visitor browsed previously.
 * - Hydrated from localStorage (no auth required).
 * - Hits a single backend call /devices-by-ids to fetch summaries.
 * - Hidden when there are no recent views (so first-time visitors see nothing).
 * - Variant: `light` (white card on home) | `dark` (used on the /refurbished hero).
 */
export default function RecentlyViewedStrip({ variant = 'light', max = 8 }) {
  const [devices, setDevices] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const ids = getRecentlyViewedIds().slice(0, max);
    if (!ids.length) { setDevices([]); setLoaded(true); return; }
    try {
      const r = await fetch(`${API_URL}/api/public/marketplace/devices-by-ids?ids=${encodeURIComponent(ids.join(','))}`);
      if (!r.ok) { setDevices([]); setLoaded(true); return; }
      const j = await r.json();
      setDevices(j.devices || []);
    } catch {
      setDevices([]);
    } finally {
      setLoaded(true);
    }
  }, [max]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when storage changes in another tab
  useEffect(() => {
    const onStorage = (e) => { if (e.key === 'refurb_recently_viewed_v1') refresh(); };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  if (!loaded || devices.length === 0) return null;

  // Defensive dedupe, cross-tab merges or hand-edited storage could produce duplicates
  const uniqueDevices = Array.from(new Map(devices.map(d => [d.id, d])).values());

  const isDark = variant === 'dark';

  const handleClear = () => { clearRecentlyViewed(); setDevices([]); };

  return (
    <section
      data-testid="recently-viewed-strip"
      className={`relative ${isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-white border-slate-200'} border-y py-8 sm:py-12`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5 sm:mb-6 gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <h2 className={`text-base sm:text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Pick up where you left off
              </h2>
              <p className={`text-[11px] sm:text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Devices you recently viewed
              </p>
            </div>
          </div>
          <button
            onClick={handleClear}
            data-testid="recently-viewed-clear"
            className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'} transition`}
            aria-label="Clear recently viewed"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>

        {/* Horizontal scroll on mobile, grid on desktop */}
        <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-1 sm:grid sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0">
          {uniqueDevices.map((d) => {
            const grade = d.condition_grade || 'B';
            const cardCls = isDark
              ? 'bg-slate-900 border-slate-800 hover:border-emerald-500/50'
              : 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg';
            return (
              <Link
                key={d.id}
                to={listingUrl(d)}
                data-testid={`recently-viewed-${d.id}`}
                className={`group flex-shrink-0 w-40 sm:w-auto block ${cardCls} border rounded-xl overflow-hidden transition-all duration-300`}
              >
                <div className={`relative aspect-square ${isDark ? 'bg-slate-800' : 'bg-slate-50'} overflow-hidden`}>
                  {d.images?.[0] ? (
                    <img
                      src={d.images[0]}
                      alt={d.device_name || `${d.brand} ${d.model}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-contain p-2 sm:p-3 group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>
                      <Package className="w-8 h-8" />
                    </div>
                  )}
                  <span className={`absolute top-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${GRADE_BG[grade] || GRADE_BG.B}`}>
                    {grade}
                  </span>
                  {d.savings_percent > 0 && (
                    <span className="absolute top-1.5 right-1.5 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                      -{d.savings_percent}%
                    </span>
                  )}
                </div>
                <div className="p-2 sm:p-3">
                  <h3 className={`text-xs sm:text-sm font-semibold line-clamp-1 ${isDark ? 'text-white group-hover:text-emerald-300' : 'text-slate-900 group-hover:text-emerald-700'} transition`}>
                    {d.device_name || `${d.brand} ${d.model}`}
                  </h3>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className={`text-sm sm:text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      ${Number(d.price).toFixed(0)}
                    </span>
                    {d.original_price && d.original_price > d.price && (
                      <span className={`text-[10px] sm:text-xs line-through ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        ${Number(d.original_price).toFixed(0)}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-5 sm:mt-6 text-center sm:text-right">
          <Link
            to="/refurbished"
            data-testid="recently-viewed-browse-all"
            className={`inline-flex items-center gap-1.5 text-sm font-semibold ${isDark ? 'text-emerald-400 hover:text-emerald-300' : 'text-emerald-700 hover:text-emerald-800'} transition group`}
          >
            Browse the full catalog
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
